import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    DEFAULT_BUSINESS_HOURS,
    DEFAULT_EMAIL_CONTACT,
    DEFAULT_PHONE_CONTACT,
    DEFAULT_SCHEDULE_URI,
} from '../constants';
import { ChatBlkBaseService } from '../services';
import {
    ChatBlkQuery,
    ChatBlkRelationshipManager,
    QueryState,
    QueryTypeEnum,
} from '../types';
import { uuidv4 } from '../utils';
import {
    fetchRecentPrompts,
    fetchRelationshipManagers,
    sendQuery,
} from './thunk';

const name = 'querySlice';

interface UserQueries {
    ids: string[];
    entries: Record<string, ChatBlkQuery>;
}
export interface UserQueryState {
    isRequiredFeedbackSubmitted?: boolean;
    queries: UserQueries;
    mostRecentQueryId: string;
    recentPrompts: { prompt: string; timestamp: string }[];
    activeQueryId: string;
    relationshipManagers: ChatBlkRelationshipManager[];
}

/**
 * The initial state of the query slice.
 */
const initialState: UserQueryState = {
    isRequiredFeedbackSubmitted: false,
    queries: {
        ids: [],
        entries: {},
    } as UserQueries,
    mostRecentQueryId: '',
    recentPrompts: [] as { prompt: string; timestamp: string }[],
    activeQueryId: '',
    relationshipManagers: [
        {
            name: '',
            email: DEFAULT_EMAIL_CONTACT,
            phone: DEFAULT_PHONE_CONTACT,
            availability: DEFAULT_BUSINESS_HOURS,
            scheduleMeetingUri: DEFAULT_SCHEDULE_URI,
            role: 'DEFAULT',
        },
    ],
};

/**
 *  Updates a query by chatExchangeId.
 *
 * @param state - The current state of the slice.
 */
const updateQuery = (
    state: UserQueryState,
    action: PayloadAction<{
        hashId: string;
        updatedProps: Partial<ChatBlkQuery>;
        queryType?: QueryTypeEnum;
    }>
) => {
    const { hashId, updatedProps } = action.payload;
    const query = state.queries.entries[hashId];
    if (query)
        state.queries.entries[hashId] = {
            ...query,
            ...updatedProps,
        } as ChatBlkQuery;
};

const setRelationshipManagers = (
    state: UserQueryState,
    action: PayloadAction<ChatBlkRelationshipManager[]>
) => {
    if (Array.isArray(action.payload)) {
        state.relationshipManagers = action.payload;
    } else {
        state.relationshipManagers = initialState.relationshipManagers;
    }
};

/**
 * Removes the most recent query from the list of queries using chatBlkEventId.
 *
 * @param state - The current state of the slice.
 */
const removeQuery = (state: UserQueryState, action: PayloadAction<string>) => {
    if (state.queries.entries[action.payload]) {
        state.queries.entries[action.payload].state = QueryState.CANCELED;
    }
    if (state.activeQueryId === action.payload) {
        state.activeQueryId = '';
    }
};

/**
 * Clears all queries from the list.
 *
 * @param state - The current state of the slice.
 */
const clearAllQueries = (state: UserQueryState) => {
    return {
        ...initialState,
        recentPrompts: [...state.recentPrompts],
    };
};

/**
 * @param state - The current state of the slice.
 * @param action - The action containing the list of recent prompts.
 */
const setInitialRecentPrompts = (
    state: UserQueryState,
    action: PayloadAction<{ prompt: string; timestamp: string }[]>
) => {
    state.recentPrompts = action.payload || [];
};

const setIsRequiredFeedbackSubmitted = (
    state: UserQueryState,
    action: PayloadAction<boolean>
) => {
    state.isRequiredFeedbackSubmitted = action.payload;
};

/**
 * Reducers for the query slice.
 */
const reducers = {
    clearAllQueries,
    setInitialRecentPrompts,
    setIsRequiredFeedbackSubmitted,
    removeQuery,
    updateQuery,
    setRelationshipManagers,
};

/**
 * A Redux slice for managing chat queries.
 */
const querySlice = createSlice({
    name,
    initialState,
    reducers,
    extraReducers: (builder) => {
        builder
            .addCase(sendQuery.pending, (state, action) => {
                state.queries.ids.forEach((id) => {
                    state.isRequiredFeedbackSubmitted = false;
                    const { state: queryState } =
                        state.queries.entries[id] || {};
                    // prevents canceling a query that is currently active because it is the most recent
                    if (queryState === QueryState.LOADING) {
                        state.queries.entries[id].state = QueryState.CANCELED;
                    }
                });

                const { hashId, chatExchangeId, prompt } = action.meta.arg;
                // If not already queued, create a new query entry
                if (!state.queries.entries[hashId]) {
                    state.queries.entries[hashId] = {
                        sessionId: ChatBlkBaseService.getInstance().sessionId, // or from action.meta.arg
                        query: prompt,
                        id: null,
                        chatOriginUri: window.location.href || '',
                        chatBlkEventId: uuidv4(),
                        userTimestamp: new Date().toISOString(),
                        state: QueryState.LOADING,
                        chatExchangeId,
                        hashId,
                    };
                    state.queries.ids.push(hashId);
                } else {
                    // If already exists, just set loading state
                    state.queries.entries[hashId].state = QueryState.LOADING;
                }

                state.activeQueryId = hashId;
                state.mostRecentQueryId = hashId;
            })
            .addCase(sendQuery.fulfilled, (state, action) => {
                const {
                    hashId,
                    output: {
                        metadata: { relationshipManagers = [] } = {
                            relationshipManagers: [],
                        },
                    } = { metadata: { relationshipManagers: [] } },
                } = action.payload || {};

                // We are updating the user's relationship managers
                // if the response contains them, otherwise we keep the existing ones
                if (
                    Array.isArray(relationshipManagers) &&
                    relationshipManagers.length > 0
                ) {
                    state.relationshipManagers = relationshipManagers;
                }

                // Update the query with the response data
                state.queries.entries[hashId] = {
                    ...state.queries.entries[hashId],
                    ...action.payload,
                };
                // Prevents adding to recent prompts
                // Only update the active query if it matches the hashId so we don't
                // accidentally clear the active query that was sent right after this one
                if (state.activeQueryId === hashId) {
                    state.activeQueryId = '';
                }
            })
            .addCase(sendQuery.rejected, (state, action) => {
                const { hashId: canceledQuery } = action.meta.arg;
                if (state.queries.entries[canceledQuery]) {
                    state.queries.entries[canceledQuery].state =
                        QueryState.CANCELED;
                }
                // Only update the active query if it matches the chatExchangeId so we don't
                // accidentally clear the active query that was sent right after this one
                if (state.activeQueryId === canceledQuery) {
                    state.activeQueryId = '';
                }
            })
            .addCase(
                fetchRelationshipManagers.fulfilled,
                (
                    state: UserQueryState,
                    action: PayloadAction<ChatBlkRelationshipManager[]>
                ) => {
                    state.relationshipManagers =
                        Array.isArray(action.payload) &&
                        action.payload.length > 0
                            ? action.payload
                            : initialState.relationshipManagers;
                }
            )
            .addCase(
                fetchRelationshipManagers.rejected,
                (state: UserQueryState) => {
                    if (!state.relationshipManagers.length) {
                        state.relationshipManagers =
                            initialState.relationshipManagers;
                    }
                }
            )
            .addCase(fetchRecentPrompts.fulfilled, (state, action) => {
                state.recentPrompts = action.payload || [];
            });
    },
});
export const queryActions = querySlice.actions;
export default querySlice;
